import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { trackEvent } from '../libs/utils';

interface ElementHighlighterProps {
  isVisible: boolean;
  onComplete?: () => void;
  onStepChange?: (step: string) => void;
}

type DemoStep = 'intro' | 'plus' | 'newEntityPlus' | 'newRequest' | 'fillBody' | 'sendFirstRequest' | 'createSecondRequest' | 'newSecondRequest' | 'fillSecondBody' | 'sendSecondRequest' | 'showResponse' | 'complete';

const DEMO_STEPS_ORDER: DemoStep[] = ['intro', 'plus', 'newEntityPlus', 'newRequest', 'fillBody', 'sendFirstRequest', 'createSecondRequest', 'newSecondRequest', 'fillSecondBody', 'sendSecondRequest', 'showResponse'];
const TOTAL_DEMO_STEPS = DEMO_STEPS_ORDER.length;

export default function ElementHighlighter({ 
  isVisible, 
  onComplete,
  onStepChange
}: ElementHighlighterProps) {
  const [currentStep, setCurrentStep] = useState<DemoStep>('intro');
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Google авторизация
  const handleGoogleLoginSuccess = async (tokenResponse: any) => {
    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: tokenResponse.access_token,
        }),
      });

      const data = await response.json();
      if (response.ok && data.access_token) {
        login(data.access_token, data.apiKey);
        setShowCompletionModal(false);
        if (onComplete) onComplete();
        navigate('/api-request');
      } else {
        setGoogleError(data.message || 'Google signup failed');
      }
    } catch (error) {
      setGoogleError('Network error. Please try again.');
      console.error('Error:', error);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: () => {
      setGoogleError('Google signup failed. Please try again.');
    },
  });

  const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M19.6 10.2273C19.6 9.51818 19.5364 8.83636 19.4182 8.18182H10V12.05H15.3818C15.15 13.3 14.4455 14.3591 13.3864 15.0682V17.5773H16.6182C18.5091 15.8364 19.6 13.2727 19.6 10.2273Z" fill="#4285F4"/>
      <path d="M10 20C12.7 20 14.9636 19.1045 16.6182 17.5773L13.3864 15.0682C12.4909 15.6682 11.3455 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.40455 11.9H1.06364V14.4909C2.70909 17.7591 6.09091 20 10 20Z" fill="#34A853"/>
      <path d="M4.40455 11.9C4.20455 11.3 4.09091 10.6591 4.09091 10C4.09091 9.34091 4.20455 8.7 4.40455 8.1V5.50909H1.06364C0.386364 6.85909 0 8.38636 0 10C0 11.6136 0.386364 13.1409 1.06364 14.4909L4.40455 11.9Z" fill="#FBBC05"/>
      <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.6909 2.60455C14.9591 0.990909 12.6955 0 10 0C6.09091 0 2.70909 2.24091 1.06364 5.50909L4.40455 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
    </svg>
  );

  // Уведомляем о изменении этапа
  useEffect(() => {
    console.log('AnimatedCursor currentStep:', currentStep);
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  useEffect(() => {
    if (!isVisible) return;

    console.log('Starting demo, isVisible:', isVisible);
    // Начинаем с интро шага
    setIsHighlighting(true);
    setCurrentStep('intro');
    console.log('Set isHighlighting to true, currentStep to intro');
  }, [isVisible]);

  // useEffect для управления подсветкой элементов
  useEffect(() => {
    if (!isHighlighting) return;

    // Удаляем подсветку со всех элементов
    removeHighlightFromElement('[data-demo="collection-plus"]', 'demo-highlight-button');
    removeHighlightFromElement('[data-demo="first-request-plus"]', 'demo-highlight-button');
    // Также удаляем по data-demo-step для надежности
    const firstRequestPlus = document.querySelector('[data-demo-step="newEntityPlus"]') as HTMLElement;
    if (firstRequestPlus) {
      firstRequestPlus.classList.remove('demo-highlight-button');
    }
    removeHighlightFromElement('[data-demo="second-request-plus"]', 'demo-highlight-button');
    // Также удаляем по data-demo-step для надежности
    const secondRequestPlus = document.querySelector('[data-demo-step="createSecondRequest"]') as HTMLElement;
    if (secondRequestPlus) {
      secondRequestPlus.classList.remove('demo-highlight-button');
    }
    removeHighlightFromElement('[data-demo="send-button"]', 'demo-highlight-send');
    removeHighlightFromElement('[data-demo="first-request"]', 'demo-highlight-request');
    removeHighlightFromElement('[data-demo="second-request"]', 'demo-highlight-request');
    removeHighlightFromElement('[data-demo="body-editor"]', 'demo-highlight-request');

    // Добавляем подсветку в зависимости от текущего шага
    console.log('Adding highlight for step:', currentStep);
    switch (currentStep) {
      case 'plus':
        console.log('Highlighting collection-plus');
        addHighlightToElement('[data-demo="collection-plus"]', 'demo-highlight-button');
        break;
      case 'newEntityPlus':
        console.log('Highlighting first-request-plus');
        // Используем селектор по data-demo-step для первой коллекции
        // Добавляем небольшую задержку, чтобы элемент успел обновиться
        setTimeout(() => {
          const element = document.querySelector('[data-demo-step="newEntityPlus"]') as HTMLElement;
          if (element) {
            element.classList.add('demo-highlight-button');
            console.log('Added demo-highlight-button to first-request-plus');
          } else {
            // Fallback на обычный селектор
            addHighlightToElement('[data-demo="first-request-plus"]', 'demo-highlight-button');
          }
        }, 100);
        break;
      case 'newRequest':
        console.log('Highlighting first-request');
        addHighlightToElement('[data-demo="first-request"]', 'demo-highlight-request');
        break;
      case 'fillBody':
        console.log('Highlighting body-editor');
        addHighlightToElement('[data-demo="body-editor"]', 'demo-highlight-request');
        break;
      case 'sendFirstRequest':
        console.log('Highlighting send-button');
        addHighlightToElement('[data-demo="send-button"]', 'demo-highlight-send');
        break;
      case 'createSecondRequest':
        console.log('Highlighting second-request-plus');
        // Используем селектор по data-demo-step для второй коллекции
        // Добавляем небольшую задержку, чтобы элемент успел обновиться
        setTimeout(() => {
          const element = document.querySelector('[data-demo-step="createSecondRequest"]') as HTMLElement;
          if (element) {
            element.classList.add('demo-highlight-button');
            console.log('Added demo-highlight-button to second-request-plus');
          } else {
            // Fallback на обычный селектор
            addHighlightToElement('[data-demo="second-request-plus"]', 'demo-highlight-button');
          }
        }, 100);
        break;
      case 'newSecondRequest':
        console.log('Highlighting second-request');
        addHighlightToElement('[data-demo="second-request"]', 'demo-highlight-request');
        break;
      case 'fillSecondBody':
        console.log('Highlighting body-editor');
        addHighlightToElement('[data-demo="body-editor"]', 'demo-highlight-request');
        break;
      case 'sendSecondRequest':
        console.log('Highlighting send-button');
        addHighlightToElement('[data-demo="send-button"]', 'demo-highlight-send');
        break;
      case 'showResponse':
        console.log('Highlighting response-viewer');
        addHighlightToElement('[data-demo="response-viewer"]', 'demo-highlight-request');
        break;
    }
  }, [currentStep, isHighlighting]);

  // Функция для добавления подсветки к элементам
  const addHighlightToElement = (selector: string, className: string) => {
    // console.log('Looking for element with selector:', selector);
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      // console.log('Found element, adding class:', className);
      // console.log('Element before adding class:', element);
      // console.log('Element classes before:', element.className);
      element.classList.add(className);
      // console.log('Element classes after:', element.className);
      // console.log('Element after adding class:', element);
      
      // Проверим, что стили действительно применяются
      // const computedStyle = window.getComputedStyle(element);
      // console.log('Computed border:', computedStyle.border);
      // console.log('Computed background-color:', computedStyle.backgroundColor);
      // console.log('Computed box-shadow:', computedStyle.boxShadow);
      
      return element;
    } else {
      console.log('Element not found with selector:', selector);
      // Давайте проверим, есть ли вообще элементы с data-demo
      const allDemoElements = document.querySelectorAll('[data-demo]');
      console.log('All elements with data-demo:', allDemoElements);
      allDemoElements.forEach((el, index) => {
        console.log(`Element ${index}:`, el, 'data-demo:', el.getAttribute('data-demo'));
      });
    }
    return null;
  };

  // Функция для удаления подсветки с элементов
  const removeHighlightFromElement = (selector: string, className: string) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      element.classList.remove(className);
    }
  };

  // Функция для программного клика на кнопку плюс
  const handleProgrammaticClick = () => {
    if (currentStep === 'plus') {
      const plusButton = document.querySelector('[data-demo="collection-plus"]') as HTMLButtonElement;
      if (plusButton) {
        console.log('Programmatically clicking collection plus button');
        plusButton.click();
        return true;
      } else {
        console.log('Collection plus button not found');
        return false;
      }
    }
    return false;
  };


  // Функция для программного клика на плюс в шаге newEntityPlus (для создания request)
  const handlePlusClickInNewEntityPlus = () => {
    if (currentStep === 'newEntityPlus') {
      const plusButton = document.querySelector('[data-demo="first-request-plus"]') as HTMLButtonElement;
      if (plusButton) {
        console.log('Programmatically clicking first request plus button');
        plusButton.click();
        return true;
      } else {
        console.log('First request plus button not found');
        return false;
      }
    }
    return false;
  };

  // Функция для программного клика на request в шаге newRequest
  const handleRequestClick = () => {
    if (currentStep === 'newRequest') {
      const requestElement = document.querySelector('[data-demo="first-request"]') as HTMLElement;
      
      if (requestElement) {
        console.log('Programmatically clicking first request element');
        requestElement.click();
        return true;
      } else {
        console.log('First request element not found');
        return false;
      }
    }
    return false;
  };

  // Функция для программного клика на кнопку Send
  const handleSendClick = () => {
    if (currentStep === 'sendFirstRequest' || currentStep === 'sendSecondRequest') {
      
      // Найти кнопку Send
      console.log(document);
      const sendButton = document.querySelector('#send-button') as HTMLButtonElement;
      
      if (sendButton) {
        console.log('Programmatically clicking Send button');
        sendButton.click();
        return true;
      } else {
        console.log('Send button not found');
        return false;
      }
    }
    return false;
  };

  // Функция для программного клика на плюс для второго запроса
  const handleSecondRequestPlusClick = () => {
    if (currentStep === 'createSecondRequest') {
      const plusButton = document.querySelector('[data-demo="second-request-plus"]') as HTMLButtonElement;
      if (plusButton) {
        console.log('Programmatically clicking plus button for second request');
        plusButton.click();
        
        // После создания запроса, убедимся что он GET
        setTimeout(() => {
          const methodDropdown = document.querySelector('[data-demo="method-dropdown"]') as HTMLButtonElement;
          if (methodDropdown) {
            methodDropdown.click();
            setTimeout(() => {
              const getOption = document.querySelector('[data-demo="get-option"]') as HTMLElement;
              if (getOption) {
                getOption.click();
                console.log('Set second request method to GET');
              } else {
                console.log('GET option not found in dropdown');
              }
            }, 100);
          } else {
            console.log('Method dropdown not found');
          }
        }, 200);
        
        return true;
      } else {
        console.log('Plus button for second request not found');
        return false;
      }
    }
    return false;
  };

  // Функция для программного клика на второй request
  const handleSecondRequestClick = () => {
    if (currentStep === 'newSecondRequest') {
      const requestElement = document.querySelector('[data-demo="second-request"]') as HTMLElement;
      
      if (requestElement) {
        console.log('Programmatically clicking second request element');
        requestElement.click();
        return true;
      } else {
        console.log('Second request element not found');
        return false;
      }
    }
    return false;
  };

  const handleElementClick = (step: DemoStep) => {
    if (currentStep !== step) return;

    switch (step) {
      case 'intro':
        // Track welcome modal OK click
        trackEvent('supaserverftueevents', { event_id: 3 });
        // После клика на интро переходим к плюсу
        setCurrentStep('plus');
        break;
      case 'plus':
        // Track + button click
        trackEvent('supaserverftueevents', { event_id: 4 });
        // После клика на плюс показываем New Entity 1 с плюсом
        setCurrentStep('newEntityPlus');
        break;
      case 'newEntityPlus':
        // Track click on + in collection
        trackEvent('supaserverftueevents', { event_id: 5 });
        // После клика на плюс в newEntityPlus показываем New Request 1
        setCurrentStep('newRequest');
        break;
      case 'newRequest':
        // Track click on new request
        trackEvent('supaserverftueevents', { event_id: 6 });
        // После клика на request показываем область body
        setCurrentStep('fillBody');
        break;
      case 'fillBody':
        // Track fill body OK click
        trackEvent('supaserverftueevents', { event_id: 7 });
        // После заполнения body показываем кнопку Send
        setCurrentStep('sendFirstRequest');
        break;
      case 'sendFirstRequest':
        // Track send first request click
        trackEvent('supaserverftueevents', { event_id: 8 });
        // После отправки первого запроса показываем плюс для второго
        setCurrentStep('createSecondRequest');
        break;
      case 'createSecondRequest':
        // Track create second request click
        trackEvent('supaserverftueevents', { event_id: 9 });
        // После создания второго запроса показываем плашку с новым запросом
        setCurrentStep('newSecondRequest');
        break;
      case 'newSecondRequest':
        // Track click on second request
        trackEvent('supaserverftueevents', { event_id: 10 });
        // После клика на второй запрос показываем body с isDone: true
        setCurrentStep('fillSecondBody');
        break;
      case 'fillSecondBody':
        // Track fill second body OK click
        trackEvent('supaserverftueevents', { event_id: 11 });
        // После просмотра body показываем кнопку Send
        setCurrentStep('sendSecondRequest');
        break;
      case 'sendSecondRequest':
        // Track send second request
        trackEvent('supaserverftueevents', { event_id: 12 });
        // После отправки второго запроса показываем response
        setCurrentStep('showResponse');
        break;
      case 'showResponse':
        // Track show response (demo complete)
        trackEvent('supaserverftueevents', { event_id: 13 });
        // Завершаем демо — показываем модалку
        setCurrentStep('complete');
        setIsHighlighting(false);
        // Очищаем подсветку
        removeHighlightFromElement('[data-demo="collection-plus"]', 'demo-highlight-button');
        removeHighlightFromElement('[data-demo="first-request-plus"]', 'demo-highlight-button');
        removeHighlightFromElement('[data-demo="second-request-plus"]', 'demo-highlight-button');
        removeHighlightFromElement('[data-demo="send-button"]', 'demo-highlight-send');
        removeHighlightFromElement('[data-demo="first-request"]', 'demo-highlight-request');
        removeHighlightFromElement('[data-demo="second-request"]', 'demo-highlight-request');
        removeHighlightFromElement('[data-demo="body-editor"]', 'demo-highlight-request');
        removeHighlightFromElement('[data-demo="response-viewer"]', 'demo-highlight-request');
        // Показываем модалку завершения вместо немедленного вызова onComplete
        setShowCompletionModal(true);
        break;
    }
  };

  const handleSkipTutorial = () => {
    setIsHighlighting(false);
    setShowCompletionModal(false);
    removeHighlightFromElement('[data-demo="collection-plus"]', 'demo-highlight-button');
    removeHighlightFromElement('[data-demo="first-request-plus"]', 'demo-highlight-button');
    removeHighlightFromElement('[data-demo="second-request-plus"]', 'demo-highlight-button');
    removeHighlightFromElement('[data-demo="send-button"]', 'demo-highlight-send');
    removeHighlightFromElement('[data-demo="first-request"]', 'demo-highlight-request');
    removeHighlightFromElement('[data-demo="second-request"]', 'demo-highlight-request');
    removeHighlightFromElement('[data-demo="body-editor"]', 'demo-highlight-request');
    removeHighlightFromElement('[data-demo="response-viewer"]', 'demo-highlight-request');
    onComplete?.();
  };

  const currentStepNumber = DEMO_STEPS_ORDER.indexOf(currentStep) + 1;

  const TooltipFooter = () => (
    <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t border-gray-100">
      <span className="text-xs text-gray-500">Step {currentStepNumber} of {TOTAL_DEMO_STEPS}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleSkipTutorial(); }}
        className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
      >
        Skip tutorial
      </button>
    </div>
  );

  if (!isVisible) return null;

  console.log('Rendering ElementHighlighter:', { isVisible, isHighlighting, currentStep });

  return (
    <>
      {/* CSS стили для подсветки */}
      <style>{`
        .demo-highlight-button {
          border: 2px solid #3b82f6 !important;
          background-color: white !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4) !important;
          border-radius: 6px !important;
          transform: scale(1.02) !important;
          transition: all 0.2s ease !important;
          animation: demoPulse 1.5s infinite !important;
          position: relative !important;
          z-index: 45 !important;
        }
        
        .demo-highlight-request {
          border: 2px solid #3b82f6 !important;
          background-color: white !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4) !important;
          border-radius: 6px !important;
          transform: scale(1.02) !important;
          transition: all 0.2s ease !important;
          animation: demoPulse 1.5s infinite !important;
          position: relative !important;
          z-index: 45 !important;
        }
        
        .demo-highlight-send {
          border: 2px solid #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4) !important;
          border-radius: 6px !important;
          transform: scale(1.02) !important;
          transition: all 0.2s ease !important;
          animation: demoPulse 1.5s infinite !important;
          position: relative !important;
          z-index: 45 !important;
        }
        
        @keyframes demoPulse {
          0%, 100% {
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4);
          }
          50% {
            box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.2);
          }
        }
      `}</style>
      
      
      {/* Интерактивные области для кликов */}
      {currentStep === 'intro' && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center" 
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }} 
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" 
            style={{ 
              maxHeight: '400px',
              marginTop: '-30vh'
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold" style={{ color: '#212121' }}>Welcome</h2>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">Step {currentStepNumber} of {TOTAL_DEMO_STEPS}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSkipTutorial(); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
                >
                  Skip tutorial
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 py-6" style={{ minHeight: 0 }}>
              <p className="text-base leading-relaxed" style={{ color: '#212121' }}>
                I'll show you how the service works with a simple todo list example
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => handleElementClick('intro')}
                className="px-6 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 transition-colors"
                style={{ cursor: 'pointer' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {currentStep === 'plus' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-4 left-4 w-8 h-8 cursor-pointer"
            onClick={() => {
              const clicked = handleProgrammaticClick();
              if (clicked) {
                handleElementClick('plus');
              }
            }}
          />
          <div 
            className="absolute top-16 left-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click the <strong>+</strong> button to create your first collection
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'newEntityPlus' && (
        <div className="fixed inset-0 z-50">
          {/* Интерактивная область для плюса */}
          <div 
            className="absolute top-16 left-58 w-8 h-8 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              const clicked = handlePlusClickInNewEntityPlus();
              if (clicked) {
                handleElementClick('newEntityPlus');
              }
            }}
          />
          <div 
            className="absolute top-28 left-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click the <strong>+</strong> button next to the collection to add your first request
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'newRequest' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-26 left-4 w-64 h-10 cursor-pointer"
            onClick={() => {
              const clicked = handleRequestClick();
              if (clicked) {
                handleElementClick('newRequest');
              }
            }}
          />
          <div 
            className="absolute top-40 left-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click on the <strong>create-task</strong> request to open and configure it
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'fillBody' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg px-5 py-4 border border-gray-200 max-w-md"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleElementClick('fillBody');
            }}
          >
            <p className="text-sm font-medium mb-3" style={{ color: '#1a1a1a' }}>
              A new not yet completed task has been added to the request body:
            </p>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 mb-3" style={{ fontFamily: 'monospace', color: '#212121' }}>
              <code>
                {`{
  "name": "use Supaserver today",
  `}
                <span 
                  style={{ 
                    backgroundColor: '#fef3c7', 
                    color: '#92400e',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}
                >
                  "isDone": false
                </span>
                {`
}`}
              </code>
            </pre>
            <TooltipFooter />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleElementClick('fillBody');
              }}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 transition-colors mt-3"
              style={{ cursor: 'pointer' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {currentStep === 'sendFirstRequest' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-22 right-4 w-24 h-14 cursor-pointer"
            onClick={() => {
              const clicked = handleSendClick();
              if (clicked) {
                handleElementClick('sendFirstRequest');
              }
            }}
          />
          <div 
            className="absolute top-38 right-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click the <strong>Send</strong> button to execute the request and create the task
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'createSecondRequest' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-16 left-58 w-8 h-8 cursor-pointer"
            onClick={() => {
              const clicked = handleSecondRequestPlusClick();
              if (clicked) {
                handleElementClick('createSecondRequest');
              }
            }}
          />
          <div 
            className="absolute top-28 left-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click the <strong>+</strong> button again to create a GET request for retrieving tasks
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'newSecondRequest' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-36 left-4 w-64 h-10 cursor-pointer"
            onClick={() => {
              const clicked = handleSecondRequestClick();
              if (clicked) {
                handleElementClick('newSecondRequest');
              }
            }}
          />
          <div 
            className="absolute top-46 left-3 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click on the <strong>make-task-done</strong> request to open it
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}
      
      {currentStep === 'fillSecondBody' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg px-5 py-4 border border-gray-200 max-w-md"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleElementClick('fillSecondBody');
            }}
          >
            <p className="text-sm font-medium mb-3" style={{ color: '#1a1a1a' }}>
              A task completion request has been added to the request body. Notice that <code className="bg-gray-100 px-1 rounded" style={{ fontSize: '11px' }}>isDone</code> is set to <code className="bg-gray-100 px-1 rounded" style={{ fontSize: '11px' }}>true</code>, which marks this task as completed:
            </p>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 mb-3" style={{ fontFamily: 'monospace', color: '#212121' }}>
              <code>
                {`{
  "name": "use Supaserver today",
  `}
                <span 
                  style={{ 
                    backgroundColor: '#d1fae5', 
                    color: '#065f46',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}
                >
                  "isDone": true
                </span>
                {`
}`}
              </code>
            </pre>
            <TooltipFooter />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleElementClick('fillSecondBody');
              }}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 transition-colors mt-3"
              style={{ cursor: 'pointer' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {currentStep === 'sendSecondRequest' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-24 right-4 w-24 h-12 cursor-pointer"
            onClick={() => {
              const clicked = handleSendClick();
              if (clicked) {
                handleElementClick('sendSecondRequest');
              }
            }}
          />
          <div 
            className="absolute top-38 right-4 bg-white rounded-lg shadow-lg px-4 py-3 border border-gray-200"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <p className="text-sm font-medium text-gray-900 m-0" style={{ color: '#1a1a1a' }}>
              Click the <strong>Send</strong> button to execute the GET request and retrieve all tasks
            </p>
            <TooltipFooter />
          </div>
        </div>
      )}

      {currentStep === 'showResponse' && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg px-5 py-4 border border-gray-200 max-w-md"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleElementClick('showResponse');
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm font-semibold" style={{ color: '#065f46' }}>Task completed!</p>
            </div>
            <p className="text-sm font-medium mb-3" style={{ color: '#1a1a1a' }}>
              The response shows the updated data. Notice that <code className="bg-gray-100 px-1 rounded" style={{ fontSize: '11px' }}>isDone</code> is now <code className="bg-green-100 px-1 rounded text-green-700" style={{ fontSize: '11px' }}>true</code> — your task has been marked as completed:
            </p>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 mb-3" style={{ fontFamily: 'monospace', color: '#212121' }}>
              <code>
                {`[
  {
    "_id": "...",
    "name": "use Supaserver today",
    `}
                <span 
                  style={{ 
                    backgroundColor: '#d1fae5', 
                    color: '#065f46',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontWeight: '600',
                    display: 'inline-block'
                  }}
                >
                  "isDone": true
                </span>
                {`
  }
]`}
              </code>
            </pre>
            <TooltipFooter />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleElementClick('showResponse');
              }}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 transition-colors mt-3"
              style={{ cursor: 'pointer' }}
            >
              Finish Demo
            </button>
          </div>
        </div>
      )}

      {/* Модалка завершения демо */}
      {showCompletionModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 100 }}
        >
          {/* Затемнение фона */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => {}}
          />
          
          {/* Модалка */}
          <div 
            className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4"
            style={{
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Заголовок */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-3">
              Demo Complete! 🎉
            </h2>
            
            {/* Описание */}
            <p className="text-center text-gray-600 mb-6 leading-relaxed">
              You've just created your first API with Supaserver! 
              Ready to build your own backend?
            </p>
            
            {/* Ошибка */}
            {googleError && (
              <p className="text-center text-red-500 text-sm mb-4">{googleError}</p>
            )}
            
            {/* Кнопка Google или Start Building */}
            {isAuthenticated ? (
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  if (onComplete) onComplete();
                  navigate('/api-request');
                }}
                className="w-full py-3 px-6 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                style={{ 
                  backgroundColor: '#1a1a1a',
                  color: '#fff',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1a1a1a';
                }}
              >
                <span>Start Building Your API</span>
              </button>
            ) : (
              <button
                onClick={() => googleLogin()}
                className="w-full py-3 px-6 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                style={{ 
                  backgroundColor: '#fff',
                  color: '#1a1a1a',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
